import React, { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { setDashboard } from "stores/rootReducer";
import playIcon from '../assets/image/playic.png';
import axios from "axios";
import { baseURL } from "Utils";

export default function PlaylistModal({ onClose }) {
  const dispatch = useDispatch();

  const [moods, setMoods] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [locations, setLocations] = useState([]);
  const [trackType, setTrackType] = useState(null);
  const [duration, setDuration] = useState(3);
  const [playlistName, setPlaylistName] = useState('');
  const [filteredPlaylists, setFilteredPlaylists] = useState([]);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [error, setError] = useState('');

  const userId = localStorage.getItem("USERID") || sessionStorage.getItem("USERID");
  const hours = [3, 6, 9, 12];

  // Fetch moods, locations, playlists + songs for default mood
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const res = await fetch(`${baseURL}api/getMoodsAndDefaultData`);
      const data = await res.json();

      if (data?.moods?.length > 0) {
        setMoods(data.moods);
        setSelectedMood(data.moods[0].id);
      }

      setLocations((data.locations || []).map(loc => ({ ...loc, selected: false })));
      setFilteredPlaylists(data.playlists || []);

      const songs = (data.playlists || []).flatMap(p => p.songs || []);
      const uniqueSongs = songs.filter((song, index, self) =>
        index === self.findIndex(s => String(s.songId) === String(song.songId))
      );
      setSelectedSongs(uniqueSongs);

    } catch (err) {
      console.error("API error:", err);
    }
  };

  // Fetch locations + playlists when mood changes
  useEffect(() => {
    if (!selectedMood) return;
    fetchMoodData();
  }, [selectedMood]);

  const fetchMoodData = async () => {
    try {
      const locRes = await fetch(`${baseURL}api/getLocationsByMood`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mood_id: selectedMood }),
      });
      const locData = await locRes.json();
      setLocations((locData.locations || []).map(loc => ({ ...loc, selected: false })));

      const plRes = await fetch(`${baseURL}api/filterPlaylists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mood_id: selectedMood }),
      });
      const plData = await plRes.json();

      setFilteredPlaylists(plData?.playlists || []);

      const songs = (plData?.playlists || []).flatMap(p => p.songs || []);
      const uniqueSongs = songs.filter((song, idx, self) =>
        idx === self.findIndex(s => String(s.songId) === String(song.songId))
      );
      setSelectedSongs(uniqueSongs);

    } catch (err) {
      console.error("Fetch mood data error:", err);
    }
  };

  // Fetch playlists when location, trackType, or duration changes
  useEffect(() => {
    if (!selectedMood) return;
    fetchFilteredPlaylists();
  }, [locations, trackType, duration]);

  const fetchFilteredPlaylists = async () => {
    try {
      const selectedLocationIds = locations.filter(loc => loc.selected).map(loc => loc.id);
      const res = await fetch(`${baseURL}api/filterPlaylists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mood_id: selectedMood,
          location_ids: selectedLocationIds.length > 0 ? selectedLocationIds : null,
          track_type: trackType,
          duration,
        }),
      });
      const data = await res.json();

      setFilteredPlaylists(data?.playlists || []);

      const songs = (data?.playlists || []).flatMap(p => p.songs || []);
      const uniqueSongs = songs.filter((song, idx, self) =>
        idx === self.findIndex(s => String(s.songId) === String(song.songId))
      );
      setSelectedSongs(uniqueSongs);

    } catch (err) {
      console.error("Filter playlists error:", err);
    }
  };

  const handleLocationChange = (id, checked) => {
    setLocations(prev =>
      prev.map(loc => (loc.id === id ? { ...loc, selected: checked } : loc))
    );
  };

  const handleGeneratePlaylist = async () => {
    if (!playlistName.trim()) {
      setError('Playlist name is required.');
      return;
    }
    if (selectedSongs.length === 0) {
      setError('Please select at least one song.');
      return;
    }

    try {
      const res = await fetch(`${baseURL}api/createPlaylist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          playlistName,
          selectedSongs: selectedSongs.map(s => s.songId),
        }),
      });
      const result = await res.json();

      if (result.success) {
        onClose();
        setUserDashboardFunc();
      } else {
        setError(result.message || 'Something went wrong.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('Server error. Please try again.');
    }
  };

  const setUserDashboardFunc = () => {
    axios
      .post(`${baseURL}api/userDashboard`, { user_id: userId })
      .then(response => dispatch(setDashboard(response?.data?.result)))
      .catch(err => console.error("Dashboard error:", err));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center aiplaylist-popup">
      <div className="bg-white rounded-lg w-[90%] max-w-5xl p-6 relative shadow-lg flex">
        <button type="button" className="popUpClose absolute font-bold" onClick={onClose}>×</button>

        {/* Left filters */}
        <div className="w-1/2 pr-6 border-r overflow-y-auto max-h-[80vh]">
          <h2 className="text-2xl font-bold mb-6">Generate Playlist</h2>

          {/* Mood Filter */}
          <div className="mb-4">
            <button className="w-full text-left font-semibold mb-1">Select by Mood</button>
            <div className="pl-4 text-sm text-gray-700 space-y-1">
              {moods.map(mood => (
                <label key={mood.id} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="mood"
                    value={mood.id}
                    checked={selectedMood === mood.id}
                    onChange={() => setSelectedMood(mood.id)}
                    className="form-radio rounded text-black"
                  />
                  <span>{mood.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Location Filter */}
          <div className="mb-4">
            <button className="w-full text-left font-semibold mb-1">Select by Location</button>
            <div className="pl-4 text-sm text-gray-700 space-y-1">
              {locations.length === 0 ? (
                <p className="text-gray-400 italic text-sm">No locations for this mood</p>
              ) : (
                locations.map(loc => (
                  <label key={loc.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={loc.id}
                      checked={loc.selected}
                      onChange={(e) => handleLocationChange(loc.id, e.target.checked)}
                      className="form-checkbox rounded text-black"
                    />
                    <span>{loc.location_name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Track Type Filter */}
          <div className="mb-4">
            <p className="font-semibold mb-2">Track Type</p>
            <div className="flex flex-col space-y-2">
              {['instrumental', 'vocal', 'both'].map(type => (
                <label key={type} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="trackType"
                    value={type}
                    checked={trackType === type}
                    onChange={() => setTrackType(type)}
                  />
                  <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Playlist Duration */}
          <div className="mb-4">
            <p className="font-semibold mb-2">Playlist Duration</p>
            <input
              type="range"
              min="0"
              max="3"
              step="1"
              value={hours.indexOf(duration)}
              onChange={e => {
                const val = hours[e.target.value];
                setDuration(val === 0 ? null : val);
              }}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-600 mt-1">
              {hours.map(h => (
                <span key={h}>{h} Hours</span>
              ))}
            </div>
          </div>
        </div>

        {/* Right side playlist and generate */}
        <div className="w-1/2 pl-6 flex flex-col">
          <input
            type="text"
            placeholder="Playlist Name"
            value={playlistName}
            onChange={e => setPlaylistName(e.target.value)}
            className="border rounded-full px-4 py-2 mb-6 w-full outline-none"
          />
          {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

          <button
            onClick={handleGeneratePlaylist}
            className="bg-black text-white py-2 rounded-full hover:bg-gray-800 transition"
          >
            Generate
          </button>

          <div className="mt-4 text-sm text-gray-700 overflow-y-auto max-h-64">
            {selectedSongs.length > 0 ? (
              <ul className="space-y-1 filtered-tracklist">
                {selectedSongs.map((song, index) => (
                  <div
                    key={song.songId || index}
                    className="flex items-center justify-between bg-[#1E1E1E] hover:bg-[#2A2A2A] rounded px-4 py-3 transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked
                        disabled
                        className="form-checkbox text-black bg-white rounded"
                      />
                      <div className="w-6 h-6 flex-shrink-0">
                        <img src={playIcon} alt="Play" className="w-full h-full object-contain" />
                      </div>
                      <span className="text-white font-medium text-sm">{song.songTitle}</span>
                    </div>
                    <span className="text-gray-400 text-xs">{song.originaltrackTime}</span>
                  </div>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm result-not-found">No tracks found!</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
